import { PROGRAMME_CODE_MAPPING } from '@constants/quiz';
import {
  SCROLL_OFFSET_TOP,
  tabsData,
  tabsDataWithSyllabus,
  TAB_INDICES,
  enrollmentDetailsTab
} from '@cont/SeeAll/constants';
import styled from '@emotion/styled';
import useScrollHandlerGroup from '@hooks/useScrollHandlerGroup';
import { useIsMobile } from '@stores/AppCommonData';
import { mediaQuery, Spacings } from '@styles/index';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TabPanel, TabPanels } from 'aquilla/core/molecules/Tabs';
import { useRouter } from 'next/router';
import Tabs from './TestSeriesTabs';
import About from './About';
import Schedule from './Schedule';
import Syllabus from './Syllabus';
import EnrollmentDetails from './EnrollmentDetails';
import TestSeriesDetails from './TestSeriesDetails';

import useTestSeriesInfo from './useTestSeriesInfo';
import useTestSeriesEvents from './useTestSeriesEvents';
import useSkuDetails from './useSkuDetails';

const InnerWrapper = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: grid;
  grid-template-columns: 1fr 336px;
  grid-column-gap: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-template-columns: 1fr;
    padding: ${Spacings.SPACING_0B};
  }
`;

const StyledTabPanels = styled(TabPanels)`
  margin-top: ${Spacings.SPACING_4B};
`;

const Sections = ({
  fromStore,
  isStickyHeaderShown,
  onOpenCbtVenueSelection
}) => {
  const { data } = useTestSeriesInfo();
  const { query } = useRouter();
  const isMobile = useIsMobile();

  const {
    expiryTime,
    isSkuAvailable,
    availableSku,
    hasSkuAccess,
    subscription
  } = useSkuDetails(data.goal?.uid, query.courseUID);

  const showEnrollmentDetails = data.enrolmentDetails?.admitCardUrl;

  const { sendViewScheduleEvent, sendViewSyllabusEvent } =
    useTestSeriesEvents(hasSkuAccess);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const aboutRef = useRef();
  const scheduleRef = useRef();
  const syllabusRef = useRef();
  const enrollmentRef = useRef();

  const refs = [
    {
      ref: aboutRef,
      id: 'ABOUT'
    },
    {
      ref: scheduleRef,
      id: 'SCHEDULE'
    },
    {
      ref: syllabusRef,
      id: 'SYLLABUS'
    },
    ...(showEnrollmentDetails ? [{ ref: enrollmentRef, id: 'ENROLLMENT' }] : [])
  ];

  const { activeStatuses, scrollToElement } = useScrollHandlerGroup(refs, {
    offsetTop: SCROLL_OFFSET_TOP
  });
  const [
    isAboutActive,
    isScheduleActive,
    isSyllabusActive,
    isEnrollmentActive = false
  ] = activeStatuses;

  const getSelectedTab = useCallback(() => {
    let newSelectedTab = selectedTabIndex;
    if (isAboutActive) {
      newSelectedTab = TAB_INDICES.FIRST_INDEX;
    } else if (isScheduleActive) {
      newSelectedTab = TAB_INDICES.SECOND_INDEX;
    } else if (isSyllabusActive) {
      newSelectedTab = TAB_INDICES.THIRD_INDEX;
    } else if (isEnrollmentActive) {
      newSelectedTab = TAB_INDICES.FOURTH_INDEX;
    }
    if (newSelectedTab === selectedTabIndex) return null;
    setSelectedTabIndex(newSelectedTab);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAboutActive, isScheduleActive, isSyllabusActive, isEnrollmentActive]);

  useEffect(() => {
    if (!isMobile) {
      getSelectedTab();
    }
  }, [getSelectedTab, isMobile]);

  const scrollToRef = (ref, id) => {
    scrollToElement(ref.current, id);
  };

  const handleChangeInMobile = (tabIndex) => {
    if (tabIndex === TAB_INDICES.SECOND_INDEX) {
      sendViewScheduleEvent();
    }
    if (tabIndex === TAB_INDICES.THIRD_INDEX) {
      sendViewSyllabusEvent();
    }
  };

  const handleChangeInDesktop = (tabIndex) => {
    if (tabIndex === TAB_INDICES.FIRST_INDEX) {
      scrollToRef(aboutRef, 'ABOUT');
    }
    if (tabIndex === TAB_INDICES.SECOND_INDEX) {
      scrollToRef(scheduleRef, 'SCHEDULE');
      sendViewScheduleEvent();
    }
    if (tabIndex === TAB_INDICES.THIRD_INDEX) {
      scrollToRef(syllabusRef, 'SYLLABUS');
      sendViewSyllabusEvent();
    }
    if (tabIndex === TAB_INDICES.FOURTH_INDEX) {
      scrollToRef(enrollmentRef, 'ENROLLMENT');
    }
  };

  const handleChange = (tabIndex) => {
    if (!isMobile) {
      handleChangeInDesktop(tabIndex);
    } else {
      handleChangeInMobile(tabIndex);
    }
    setSelectedTabIndex(tabIndex);
  };

  const getTabsData = () => {
    const tabs =
      data.programmeSubtype === PROGRAMME_CODE_MAPPING.FULL_SYLLABUS
        ? tabsDataWithSyllabus
        : tabsData;
    return showEnrollmentDetails ? [...tabs, enrollmentDetailsTab] : tabs;
  };

  return (
    <div>
      <Tabs
        tabsData={getTabsData()}
        onChange={handleChange}
        selectedTabIndex={selectedTabIndex}
      >
        {isMobile && (
          <StyledTabPanels>
            <TabPanel>
              <About isPurchaseActive={hasSkuAccess} />
            </TabPanel>
            <TabPanel>
              <Schedule
                fromStore={fromStore}
                expiryTime={expiryTime}
                isSkuAvailable={isSkuAvailable}
                availableSku={availableSku}
                hasSkuAccess={hasSkuAccess}
                subscription={subscription}
              />
            </TabPanel>
            <TabPanel>
              {data?.programmeSubtype ===
                PROGRAMME_CODE_MAPPING.FULL_SYLLABUS && <Syllabus />}
            </TabPanel>
            <TabPanel>
              {showEnrollmentDetails && <EnrollmentDetails />}
            </TabPanel>
          </StyledTabPanels>
        )}
      </Tabs>

      {!isMobile && (
        <InnerWrapper>
          <div>
            <About ref={aboutRef} isPurchaseActive={hasSkuAccess} />
            <Schedule
              ref={scheduleRef}
              fromStore={fromStore}
              expiryTime={expiryTime}
              isSkuAvailable={isSkuAvailable}
              availableSku={availableSku}
              hasSkuAccess={hasSkuAccess}
              subscription={subscription}
            />
            {data.programmeSubtype === PROGRAMME_CODE_MAPPING.FULL_SYLLABUS && (
              <Syllabus ref={syllabusRef} />
            )}
            {showEnrollmentDetails && <EnrollmentDetails ref={enrollmentRef} />}
          </div>

          <TestSeriesDetails
            isSticky
            fromStore={fromStore}
            isHeaderShown={isStickyHeaderShown}
            onOpenCbtVenueSelection={onOpenCbtVenueSelection}
          />
        </InnerWrapper>
      )}
    </div>
  );
};

export default Sections;
